import _ from 'underscore';
import Backbone from 'backbone';
import Api from '@/lib/api/api';
import Moment from 'moment';
import v5_Model from '@/models/v5/model';
import v5_LogsModel from '@/models/v5/logs';
import v5_Collection from '@/collections/v5/collection';
import BandwidthModel from '@/models/v5/bandwidth';

var device_type_strings = {
    development_devicegroup: 'Development',
    pre_factoryfixture_devicegroup: 'Test Fixture',
    pre_production_devicegroup: 'Test Production Device',
    factoryfixture_devicegroup: 'Factory Fixture',
    production_devicegroup: 'Production',
    dut: 'Device Under Test',
    dut_devicegroup: 'Device Under Test',
    pre_dut_devicegroup: 'Test Device Under Test',
    unassigned: 'Unassigned'
};

var HistoryEntry = Backbone.Model.extend({
    parse: function (attrs) {
        return _.extend({
            id: attrs.timestamp + attrs.event
        }, attrs);
    }
});

function create_history_collection(device) {
    var c = new v5_Collection();
    c.url = function () { return Api.urls.history({ device_id: device.id }); };
    c.comparator = function(history_item) {
        var ts = Moment(history_item.get('timestamp'));
        return -Number(ts.format('x'));
    };
    c.parse = function parse(models) {
        models = v5_Collection.prototype.parse(models);
        return models.map(function (model) {
            return HistoryEntry.prototype.parse(model);
        });
    };
    return c;
}

function set_device_type(device) {
    var key;
    var group = device.rel.get('devicegroup');
    
    if (group) {
        key = group.type;
    } else {
        key = 'unassigned';
    }
    device.set('device_type', device_type_strings[key]);
    device.set('devicegroup', group);
}
var Device = v5_Model.extend({
    defaults: {
        agent_id: "",
        agent_running: false,
        agent_state_changed_at: null,
        agent_url: "",
        device_online: false,
        device_state_changed_at: "",
        free_memory: null,
        imp_type: "",
        ip_address: "",
        last_blinkup_at: "",
        mac_address: "",
        name: null,
        plan_id: "",
        rssi: null,
        swversion: "",
        device_type: "",
        iccid: null
    },
    initialize: function() {
        this.type = Device.type;
        if (this.id === null) {
            this.destroy();
            return;
        }
        this.logs = new v5_LogsModel(this);
        /*
            *  Limit the size of a Logs Collection
            *  WEB-1370, code views are killing the
            *  browser when they get too large.
        */
        this.on('change:logs', function(self){
            while ( self.logs.length > 1100 ) {
                self.logs.shift();
            }
        });

        this.history = create_history_collection(this);
        this.bandwidth = new BandwidthModel(this);
        // device_type is a special property not returned
        // by the API and related to the devicegroup that
        // the device belongs to.
        this.listenTo(
            this.rel,
            'change:devicegroup',
            _.partial(set_device_type, this)
        );
        set_device_type(this);
    },
    /**
     * Conditionally restart the device
     *
     * @param options like $.ajax options
     */
    conditional_restart: function(options){
        options = _.extend({
            type: 'POST',
            url: Api.urls.device_conditional_restart({ id: this.id })
        }, options);
        return Api.ajax_v5(options);
    },
    /**
     * Restart the device.
     *
     * @param options like $.ajax options
     */
    restart: function(options) {
        options = _.extend({
            type: 'POST',
            url: Api.urls.device_restart({ id: this.id })
        }, options);
        return Api.ajax_v5(options);
    },
    /**
     * Unassign the device from its devicegroup, if it has one
     */
    unassign: function(options) {
        options = _.clone(options) || {};
        var devicegroup = this.rel.get('devicegroup');
        if (!devicegroup || !devicegroup.unassign) {
            console.warn("No devicegroup for device %d", this.id);
            var id = this.id;
            if (options.error) {
                return options.error(
                    {
                        responseJSON: {
                            errors: [
                                {
                                    id: id,
                                    status: '',
                                    code: 'FAILED',
                                    title: 'Unassigned Device',
                                    detail: 'Unassigned Devices cannot be unassigned.',
                                    is_error: true,
                                }
                            ]
                        }
                    }
                );
            }
        }
        var success = options.success;
        var error = options.error;
        options.success = function(model, resp, options) {
            if (success) success.call(options.context, model, resp, options);
        };
        options.error = function(resp, options) {
            if (error) error.apply(options.context, arguments);
        };
        return devicegroup.unassign([this], options);
    },
    destroy: function(options) {
        var devicegroup = this.rel.get('devicegroup');

        // Needed to handle special "test blessed device" case
        // These are "deleted" in that they are removed from the account... on unassign
        // No DELETE request is desired

        if (devicegroup && (devicegroup.type === 'pre_production_devicegroup' || devicegroup.type === 'pre_dut_devicegroup')) {
            this.trigger('destroy', this, this.collection, options);
            this.stopListening();
            return this.unassign.call(this, options);
        }

        // Meanwhile, unassigned devices send a DELETE request to the API
        return Backbone.Model.prototype.destroy.call(this, options);
    },
    /**
     * Return a human-readable string indicating the type of the device.
     * This isn't an API type, this is a UI-facing type.
     */
    human_type: function(flags) {
        var key;
        if (this.get('is_dut')) {
            key = 'dut';
        } else if (!this.rel.get('devicegroup')) {
            key = 'unassigned';
        } else {
            key = this.rel.get('devicegroup').type;
            if ( 
                this.rel.get('devicegroup').type === 'pre_production_devicegroup' 
                && (flags && flags.show_enrolled)) {
                    if (this.get('agent_id')) {
                        key = 'pre_production_devicegroup_enrolled';
                    } else {
                        key = 'pre_production_devicegroup_unenrolled';
                    }
            }
        }
        return ({
            unassigned: 'Unassigned',
            dut: 'Device Under Test',
            development_devicegroup: 'Development Device',
            pre_factoryfixture_devicegroup: 'Test Fixture',
            pre_production_devicegroup: 'Test Production Device',
            pre_production_devicegroup_enrolled: 'Test Production Device - Enrolled',
            pre_production_devicegroup_unenrolled: 'Test Production Device - Unenrolled',
            factoryfixture_devicegroup: 'Factory Fixture',
            production_devicegroup: 'Production Device',
            production_devicegroup_enrolled: 'Production Device - Enrolled',
            production_devicegroup_unenrolled: 'Production Device - Unenrolled',
        })[key];
    },
    /**
     * Add 'human_type' to output of a Device's toJSON call
     */
    toJSON: function() {
        return _.extend({
            human_type: this.human_type()
        }, v5_Model.prototype.toJSON.call(this));
    }
});
Device.type = 'device';
export default Device;

